import {
    Avatar,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    SelectItem,
    Textarea,
    useDisclosure
} from "@heroui/react";
import {useForm} from "@mantine/form";
import {useMutation} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import toast from 'react-hot-toast';
import $http from "../helpers/axios.jsx";
import {Dropzone, DropzoneAccept, DropzoneIdle, DropzoneReject, IMAGE_MIME_TYPE} from '@mantine/dropzone';
import PageLoader from "./PageLoader.jsx";
import _ from "lodash";

const ContactUsForm = ({email = ""}) => {
    const {isOpen: confirmModalIsOpen, onOpenChange: confirmModalOnOpenChange} = useDisclosure();

    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
    const MAX_TOTAL_FILES = 4;
    const MAX_TOTAL_SIZE = 4 * 1024 * 1024; // 4MB
    const [attachments, setAttachments] = useState([]);


    const topics = [
        {key: "dubbing-support", label: "Dubbing Support"},
        {key: "voice-cloning-support", label: "Voice Cloning Support"},
        {key: "text-to-speech-support", label: "Text-to-Speech Support"},
        {key: "billing-subscriptions", label: "Billing & Subscriptions"},
        {key: "technical-support", label: "Technical Support"},
        {key: "feature-request", label: "Feature Request"},
        {key: "bug-report", label: "Bug Report"},
        {key: "general", label: "General Inquiry"}
    ];

    const initialValues = {
        email: "",
        topic: "",
        subject: "",
        message: ""
    };

    const form = useForm({
        initialValues: initialValues,
        validate: {
            email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
            topic: (value) => (value ? null : 'Please select a topic'),
            subject: (value) => (value ? null : 'Subject is required'),
            message: (value) => (value ? null : 'Message is required')
        }
    });


    const handleDrop = (acceptedFiles) => {
        const allFiles = [...attachments, ...acceptedFiles];

        // Check number of files
        if (allFiles.length > MAX_TOTAL_FILES) {
            toast.error(`You can only upload up to ${MAX_TOTAL_FILES} files.`);
            return;
        }

        // Check individual file size
        const oversized = acceptedFiles.find((file) => file.size > MAX_FILE_SIZE);
        if (oversized) {
            toast.error(`Each file must be 1MB or smaller.`);
            return;
        }

        // Check total size
        const totalSize = allFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
            toast.error(`Total file size must not exceed 4MB.`);
            return;
        }

        setAttachments(allFiles);
    };

    const handleReject = (rejections) => {
        rejections.forEach(({file, errors}) => {
            errors.forEach((error) => {
                let message = error.message;

                // Convert "1048576 bytes" to "1 MB" if it exists in the message
                const byteMatch = message.match(/(\d+)\s*bytes/);
                if (byteMatch) {
                    const bytes = parseInt(byteMatch[1], 10);
                    const mb = (bytes / 1024 / 1024).toFixed(0);
                    message = message.replace(
                        byteMatch[0],
                        `${mb} MB`
                    );
                }
                toast.error(message);
            });
        });
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const contactMutation = useMutation({
        mutationKey: ['contact-us'],
        mutationFn: (payload) => {
            return $http.post('/contact/submit', payload, {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            }).then(res => res.data);
        },
        onSuccess: (response) => {
            toast.success("Your message has been sent successfully!");
            form.reset();
            setAttachments([]);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || 'Failed to send message. Please try again.');
        }
    });

    const submitForm = () => {
        const formData = new FormData();
        formData.append("email", form.values.email);
        formData.append("topic", form.values.topic);
        formData.append("subject", form.values.subject);
        formData.append("message", form.values.message);

        attachments.forEach((attachment) => {
            formData.append("attachments", attachment);
        });

        contactMutation.mutate(formData);
    };

    const handleSubmit = () => {
        const validation = form.validate();
        if (!validation.hasErrors) {
            confirmModalOnOpenChange();
        }
    };

    const isFormValid = () => {
        return form.values.email &&
            form.values.topic &&
            form.values.subject &&
            form.values.message &&
            /^\S+@\S+$/.test(form.values.email);
    };

    useEffect(() => {
        if (email) {
            form.setFieldValue('email', email);
        }
    }, [email]);

    return (
        <>
            <div className="w-full transition duration-200 ease-out mt-2">
                <div className="mt-6 flex gap-8 flex-col">
                    {/* Email Address */}
                    <div>
                        <Input
                            label={<p className="font-medium text-[15px] mb-2 text-slate-800 flex items-center gap-2.5">
                                <img className="inline w-6" src="/images/one-square-stroke-rounded.svg" alt="step 3"/>
                                Email Address
                            </p>}
                            labelPlacement="outside"
                            isRequired
                            type="email"
                            value={form.values.email}
                            onChange={(e) => form.setFieldValue('email', e.target.value)}
                            classNames={{
                                inputWrapper: "h-[42px] px-4 w-full bg-[#fcfcfd] focus-within:!border-slate-800 focus-within:!bg-[#fcfcfd] hover:!bg-transparent rounded-xl border border-[#12131714] focus-within:border-slate-800",
                                input: "!text-sm !font-light text-gray-700 placeholder:text-gray-400 outline-none",
                                label: "flex",
                            }}
                            placeholder="Enter your email address"
                            isInvalid={form.errors.email}
                            errorMessage={form.errors.email}
                        />
                    </div>

                    {/* Topic Selection */}
                    <div>
                        <Select
                            label={<p className="font-medium text-[15px] mb-2 text-slate-800 flex items-center gap-2.5">
                                <img className="inline w-6" src="/images/two-square-stroke-rounded.svg" alt="step 3"/>
                                Select Topic
                            </p>}
                            labelPlacement="outside"
                            isRequired
                            placeholder="Choose a topic"
                            value={form.values.topic}
                            onSelectionChange={(value) => {
                                form.setFieldValue('topic', value?.anchorKey)
                            }}
                            classNames={{
                                trigger: "h-[42px] focus:border-slate-800 px-4 w-full bg-[#fcfcfd] rounded-xl text-sm border-1 border-[#12131714] outline-0",
                                value: "text-gray-700 !font-light",
                                listbox: "text-gray-700 !font-md",
                                listboxWrapper: "!p-0",
                                label: "flex",
                            }}
                            isInvalid={form.errors.topic}
                            errorMessage={form.errors.topic}
                        >
                            {topics.map((topic) => (
                                <SelectItem key={topic.key} value={topic.key}>
                                    {topic.label}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>

                    {/* Subject */}
                    <div>
                        <Input
                            label={<p className="font-medium text-[15px] mb-2 text-slate-800 flex items-center gap-2.5">
                                <img className="inline w-6" src="/images/three-square-stroke-rounded.svg" alt="step 3"/>
                                Subject
                            </p>}
                            labelPlacement="outside"
                            isRequired
                            value={form.values.subject}
                            onChange={(e) => {
                                if (e.target.value.length <= 100) {
                                    form.setFieldValue('subject', e.target.value);
                                }
                            }}
                            classNames={{
                                inputWrapper: "!shadow-none h-[42px] px-4 w-full bg-[#fcfcfd] focus-within:!border-slate-800 focus-within:!bg-[#fcfcfd] hover:!bg-transparent rounded-xl border border-[#12131714] focus-within:border-slate-800",
                                input: "!text-sm !font-light text-gray-700 placeholder:text-gray-400 outline-none",
                                label: "flex",
                            }}
                            placeholder="Brief description of your inquiry"
                            isInvalid={form.errors.subject}
                            errorMessage={form.errors.subject}
                            endContent={
                                <span
                                    className="text-sm font-light text-gray-400 flex justify-center items-center whitespace-nowrap">
                                    {form.values.subject?.length || 0} / 100
                                </span>
                            }
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <Textarea
                            label={<p className="font-medium text-[15px] mb-2 text-slate-800 flex items-center gap-2.5">
                                <img className="inline w-6" src="/images/four-square-stroke-rounded.svg" alt="step 4"/>
                                Your Message
                            </p>}
                            labelPlacement="outside"
                            isRequired
                            value={form.values.message}
                            onChange={(e) => {
                                if (e.target.value.length <= 1000) {
                                    form.setFieldValue('message', e.target.value);
                                }
                            }}
                            classNames={{
                                inputWrapper: "!shadow-none min-h-[120px] px-4 py-3 w-full bg-[#fcfcfd] focus-within:!border-slate-800 focus-within:!bg-[#fcfcfd] hover:!bg-transparent rounded-xl border border-[#12131714] focus-within:border-slate-800",
                                input: "!text-sm !font-light text-gray-700 placeholder:text-gray-400 outline-none resize-none",
                                label: "flex",
                            }}
                            placeholder="Please describe your issue or question in detail..."
                            isInvalid={form.errors.message}
                            errorMessage={form.errors.message}
                            endContent={
                                <span className="text-sm font-light text-gray-400 absolute bottom-3 right-6">
                                    {form.values.message?.length || 0} / 1000
                                </span>
                            }
                        />
                    </div>

                    {/* Attachments */}
                    <div>
                        <p className="font-medium text-[15px] mb-2 text-slate-800 flex items-center gap-2.5">
                            <img className="inline w-6" src="/images/five-square-stroke-rounded.svg" alt="step 5"/>
                            Attachments (Optional)
                        </p>
                        <div className="flex flex-col gap-3">
                            <div
                                className="text-center grow h-[initial] border-dashed border-2  overflow-hidden border-slate-500/30 rounded-20 cursor-pointer flex flex-col items-center justify-center py-5">
                                <Dropzone
                                    classNames={{
                                        root: 'w-full h-full',
                                        inner: 'w-full h-full flex items-center'
                                    }}
                                    accept={IMAGE_MIME_TYPE}
                                    maxSize={MAX_FILE_SIZE}
                                    onDrop={handleDrop}
                                    onReject={handleReject}
                                    multiple
                                >
                                    <DropzoneAccept>
                                        <div className="w-full bg-green-50 h-full rounded-20">
                                            <PageLoader/>
                                        </div>
                                    </DropzoneAccept>
                                    <DropzoneReject>
                                        <div className="w-full">
                                            <i className="hgi-stroke hgi-image-not-found-01 text-red-500 text-5xl max-w-24 mx-auto mb-5"></i>
                                            <h2 className="font-sans font-bold text-medium">
                                                This file is not supported, Please upload a valid file.
                                            </h2>
                                            <p className="text-sm text-slate-500 mt-2 mb-5">
                                                <p className="text-sm text-slate-500 mt-2 mb-5">PNG, JPG, JPEG, WebP
                                                    (MAX. 1MB each)</p>
                                            </p>
                                        </div>
                                    </DropzoneReject>
                                    <DropzoneIdle>
                                        {_.isEmpty(attachments) ?
                                            <div className="w-full">
                                                <div
                                                    className="text-center flex items-center justify-center">
                                                    <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true"
                                                         xmlns="http://www.w3.org/2000/svg" fill="none"
                                                         viewBox="0 0 20 16">
                                                        <path stroke="currentColor" strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                                                    </svg>
                                                </div>

                                                <h2 className="font-sans font-bold text-lg">Upload file, drag or drop
                                                    here</h2>
                                                <p className="text-xs text-slate-500 mt-2 mb-5">PNG, JPG, JPEG, WebP
                                                    (MAX. 1MB each)</p>
                                            </div>
                                            :
                                            <div
                                                className="w-full flex flex-col items-center justify-center gap-6 py-3">
                                                <h2 className="font-sans font-bold text-xl">
                                                    Selected files
                                                </h2>
                                                <div className="flex gap-5 justify-center grow w-full">
                                                    {attachments.map((file, index) => (
                                                        <div key={index} className="relative">
                                                            <Avatar
                                                                title={file.name}
                                                                radius="sm" isBordered size="lg"
                                                                src={URL.createObjectURL(file)}
                                                            />
                                                            <Button
                                                                onPress={() => removeAttachment(index)}
                                                                className="absolute -top-3 -right-3 scale-75"
                                                                isIconOnly radius="full" color="danger"
                                                                variant="solid" size="sm">
                                                                <i className="hgi hgi-stroke hgi-cancel-01 text-lg"></i>
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        }
                                    </DropzoneIdle>
                                </Dropzone>
                            </div>
                            {/*    <div className="flex items-center justify-center w-full">*/}
                            {/*        <label*/}
                            {/*            htmlFor="file-upload"*/}
                            {/*            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl transition ${*/}
                            {/*                attachments.length >= 4*/}
                            {/*                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'*/}
                            {/*                    : 'border-gray-300 bg-[#fcfcfd] hover:border-[#8a4af3] hover:bg-gray-50 cursor-pointer'*/}
                            {/*            }`}*/}
                            {/*        >*/}
                            {/*            <div className="flex flex-col items-center justify-center pt-5 pb-6">*/}
                            {/*                <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true"*/}
                            {/*                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">*/}
                            {/*                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"*/}
                            {/*                          strokeWidth="2"*/}
                            {/*                          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>*/}
                            {/*                </svg>*/}
                            {/*                <p className="mb-2 text-sm text-gray-500">*/}
                            {/*<span className="font-semibold">*/}
                            {/*    {attachments.length >= 4 ? 'Maximum files reached' : 'Click to upload'}*/}
                            {/*</span>*/}
                            {/*                    {attachments.length < 4 && ' or drag and drop'}*/}
                            {/*                </p>*/}
                            {/*                <p className="text-xs text-gray-500">*/}
                            {/*                    PNG, JPG, JPEG, WebP (MAX. 1MB each) - {attachments.length}/4 images*/}
                            {/*                </p>*/}
                            {/*            </div>*/}
                            {/*            <input*/}
                            {/*                id="file-upload"*/}
                            {/*                type="file"*/}
                            {/*                className="hidden"*/}
                            {/*                multiple*/}
                            {/*                accept="image/*"*/}
                            {/*                onChange={handleFileUpload}*/}
                            {/*                disabled={attachments.length >= 4}*/}
                            {/*            />*/}
                            {/*        </label>*/}
                            {/*    </div>*/}

                            {/* Attachments List */}
                            {/*{attachments.length > 0 && (*/}
                            {/*    <div className="bg-[#c8ceda0f] border-1 border-[#c8ceda26] p-3 rounded-xl">*/}
                            {/*        <p className="text-sm font-medium text-slate-800 mb-2">Attached Files*/}
                            {/*            ({attachments.length}/4)</p>*/}
                            {/*        <div className="space-y-2">*/}
                            {/*            {attachments.map((attachment) => (*/}
                            {/*                <div key={attachment.id}*/}
                            {/*                     className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200">*/}
                            {/*                    <div className="flex items-center space-x-2">*/}
                            {/*                        <svg className="w-4 h-4 text-gray-500" fill="currentColor"*/}
                            {/*                             viewBox="0 0 20 20">*/}
                            {/*                            <path fillRule="evenodd"*/}
                            {/*                                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"*/}
                            {/*                                  clipRule="evenodd"/>*/}
                            {/*                        </svg>*/}
                            {/*                        <span*/}
                            {/*                            className="text-sm text-gray-700 truncate max-w-[200px]">{attachment.name}</span>*/}
                            {/*                        <span*/}
                            {/*                            className="text-xs text-gray-500">({formatFileSize(attachment.size)})</span>*/}
                            {/*                    </div>*/}
                            {/*                    <button*/}
                            {/*                        onClick={() => removeAttachment(attachment.id)}*/}
                            {/*                        className="text-red-500 hover:text-red-700 text-sm"*/}
                            {/*                    >*/}
                            {/*                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">*/}
                            {/*                            <path fillRule="evenodd"*/}
                            {/*                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"*/}
                            {/*                                  clipRule="evenodd"/>*/}
                            {/*                        </svg>*/}
                            {/*                    </button>*/}
                            {/*                </div>*/}
                            {/*            ))}*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*)}*/}
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-4 mt-5">
                        <Button
                            disabled={contactMutation.isPending}
                            onPress={() => {
                                if (!contactMutation.isPending) {
                                    form.reset();
                                    setAttachments([]);
                                }
                            }}
                            className={`w-[156px] text-sm bg-[#c8ceda4d] border-1 border-[#00000014] hover:border-slate-700 rounded-xl 
                            ${contactMutation.isPending ? 'opacity-30 hover:border-slate-200 hover:text-slate-500 cursor-not-allowed' : ''}`}
                        >
                            Clear Form
                        </Button>
                        <Button
                            isLoading={contactMutation.isPending}
                            onPress={handleSubmit}
                            className={`w-[156px] text-[#f8f8f8] text-sm bg-[#7659fa] border-1 border-[#00000014] hover:border-slate-700 rounded-xl ${
                                !isFormValid() ? 'opacity-40 cursor-not-allowed hover:bg-[#b5a7f2] border-0' : ''
                            }`}
                            isDisabled={!isFormValid()}
                        >
                            <i className="hgi font-bold text-[16px] hgi-stroke hgi-send"></i> Send Message
                        </Button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <Modal
                isOpen={confirmModalIsOpen}
                onOpenChange={confirmModalOnOpenChange}
                size="lg"
                hideCloseButton
                backdrop="blur"
                classNames={{
                    base: "overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl",
                    body: "p-0",
                }}
            >
                <ModalContent>
                    {(onClose) => (
                        <ModalBody>
                            {/* Hero band with animated paper plane on a dotted flight path */}
                            <div className="relative overflow-hidden bg-gradient-to-br from-[#7659fa] via-[#8a6cff] to-[#a78bfa] px-8 pt-9 pb-12">
                                {/* decorative blurred blobs */}
                                <div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl"/>
                                <div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl"/>

                                {/* dotted flight path */}
                                <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
                                     preserveAspectRatio="none" viewBox="0 0 400 160" fill="none">
                                    <path d="M20 130 C 120 40, 240 150, 380 30" stroke="white" strokeWidth="2"
                                          strokeLinecap="round" strokeDasharray="2 10"/>
                                </svg>

                                <div className="relative flex flex-col items-center text-center">
                                    <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40 animate-[bounce_2.2s_ease-in-out_infinite]">
                                        <i className="hgi hgi-stroke hgi-mail-01 text-3xl text-white"/>
                                    </div>
                                    <h2 className="mt-4 text-xl font-semibold text-white">Ready to send?</h2>
                                    <p className="mt-1 max-w-sm text-sm font-light text-white/80">
                                        Quick check before this lands in our inbox. We typically reply within 24 hours.
                                    </p>
                                </div>
                            </div>

                            {/* Summary card lifted over the hero band */}
                            <div className="relative -mt-6 px-6 pb-2">
                                <div className="rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4 shadow-sm">
                                    {/* Recipient */}
                                    <div className="flex items-center gap-3">
                                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#7659fa1a] text-[#7659fa]">
                                            <i className="hgi hgi-stroke hgi-mail-01 text-lg"/>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">From</p>
                                            <p className="truncate text-sm font-medium text-slate-800">{form.values.email}</p>
                                        </div>
                                    </div>

                                    <div className="my-3 h-px bg-[#12131714]"/>

                                    {/* Topic + Subject */}
                                    <div className="flex items-start gap-3">
                                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#7659fa1a] text-[#7659fa]">
                                            <i className="hgi hgi-stroke hgi-message-01 text-lg"/>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                {topics.find((t) => t.key === form.values.topic)?.label && (
                                                    <span className="inline-flex items-center rounded-full bg-[#7659fa] px-2.5 py-0.5 text-[11px] font-medium text-white">
                                                        {topics.find((t) => t.key === form.values.topic)?.label}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1.5 truncate text-sm font-semibold text-slate-800">
                                                {form.values.subject}
                                            </p>
                                            <p className="mt-0.5 line-clamp-2 text-xs font-light leading-relaxed text-gray-500">
                                                {form.values.message}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Attachments */}
                                    {attachments.length > 0 && (
                                        <>
                                            <div className="my-3 h-px bg-[#12131714]"/>
                                            <div className="flex items-center gap-3">
                                                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#7659fa1a] text-[#7659fa]">
                                                    <i className="hgi hgi-stroke hgi-attachment-01 text-lg"/>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex -space-x-2">
                                                        {attachments.slice(0, 4).map((file, index) => (
                                                            <Avatar
                                                                key={index}
                                                                title={file.name}
                                                                radius="md"
                                                                size="sm"
                                                                isBordered
                                                                className="ring-2 ring-white"
                                                                src={URL.createObjectURL(file)}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-xs font-light text-gray-500">
                                                        {attachments.length} {attachments.length === 1 ? "file" : "files"} attached
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 px-6 py-5">
                                <Button
                                    variant="bordered"
                                    className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-5 text-sm font-medium text-slate-700 hover:border-slate-700"
                                    onPress={confirmModalOnOpenChange}
                                >
                                    Go back
                                </Button>
                                <Button
                                    isLoading={contactMutation.isPending}
                                    className="h-11 rounded-xl bg-[#7659fa] px-6 text-sm font-medium text-[#f8f8f8] shadow-lg shadow-[#7659fa40] transition hover:opacity-95"
                                    onPress={() => {
                                        submitForm();
                                        confirmModalOnOpenChange();
                                    }}
                                >
                                    <i className="hgi hgi-stroke hgi-send text-[16px] font-bold"/> Confirm &amp; Send
                                </Button>
                            </div>
                        </ModalBody>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
};

export default ContactUsForm;